import {AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, NavigationStart, ParamMap, Router} from '@angular/router';
import {ToolsService} from '@app/services/tools/tools.service';
import {ContentService, EventsService} from '@app/services/services';
import {TranslateService} from '@ngx-translate/core';
import {TopicVal, FileTypeVal, RootFileCodes, Topic} from '@app/shared/enums';
import {Subscription} from 'rxjs';
import {File} from '@app/models/content';
import {environment} from '@env/environment';

@Component({
  selector: 'app-hub',
  templateUrl: './hub.component.html',
  styleUrls: ['./hub.component.scss']
})
export class HubComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('heroHub') heroEl: ElementRef;

  topic: string;
  type: string;
  pageTitle: string;
  heroImageSrc: string;
  watcher: any;
  subscriptions: Subscription[] = [];
  filterKeywords: string;
  rootFile: File;

  constructor(
    public router: Router,
    public events: EventsService,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private toolsService: ToolsService,
    private contentService: ContentService
  ) {}

  ngOnInit() {

    this.subscriptions.push(
      this.route.paramMap.subscribe((params: ParamMap) => {
        const reversedRoute = this.translate.instant(
          `Common.Library.ReverseRoutes.${params.get('hub')}`
        );

        // Todo: english test only there in case of going live in urgency
        const isEnglish = ( reversedRoute === Topic.english);
        const isOther = ( reversedRoute === Topic.others );

        if ( ( environment.production && isEnglish ) || isOther  ) {
          this.router.navigate(['/404'], {replaceUrl: true});
          return;
        }

        const isTopic = TopicVal.hasOwnProperty(reversedRoute);
        const isType = FileTypeVal.hasOwnProperty(reversedRoute);

        if (isTopic || isType) {

          this.heroImageSrc = `assets/images/hubs/hub-header_${reversedRoute}.svg`;

          if (isTopic) {
            this.topic = reversedRoute;
            this.toolsService.setBodyAttribute('data-theme', this.topic);
          } else {
            this.type = reversedRoute;
            this.toolsService.setBodyAttribute('data-theme', 'default');
          }

          this.pageTitle = this.translate.instant('Common.Library.Hubs.' + reversedRoute);

          this.loadRootFile();

          if (this.watcher === null) {
            this.createObserver();
          }
        } else {
          this.router.navigate(['/404'], {replaceUrl: true});
        }
      })
    );

    this.subscriptions.push(
      this.router.events.subscribe(
        event => {
          if (event instanceof NavigationStart) {
            if (this.watcher) {
              this.watcher.unobserve(this.heroEl.nativeElement);
              this.watcher = null;
            }

            const shouldHideFloOptions = {
              shouldHide: true,
              from: 'nav'
            };
            this.events.broadcast('shouldHideFlo:isIntersecting', shouldHideFloOptions);
          }
        }
      )
    );

    this.subscriptions.push(
      this.route.queryParams.subscribe((params) => {
        if (params.hasOwnProperty('keywords')) {
          this.filterKeywords = params.keywords;
        }
      })
    );
  }

  ngAfterViewInit() {
    this.createObserver();
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());

    if (this.watcher) {
      this.watcher.unobserve(this.heroEl.nativeElement);
      this.watcher = null;
    }
  }

  loadRootFile() {
    const rootFileCode = RootFileCodes[this.topic || this.type];
    if (rootFileCode) {
      this.subscriptions.push(
        this.contentService.getFile(this.translate.currentLang, rootFileCode)
          .subscribe(
            result => {
              if (!result.errors) {
                this.rootFile = result.data.file;
              }
            }
          )
      );
    }
  }

  createObserver() {
    this.watcher = this.toolsService.createWatcher({
        root: null,
        rootMargin: '0px',
        threshold: 0.0
      },
      this.update.bind(this)
    );
    this.watcher.observe(this.heroEl.nativeElement);
  }

  update(status) {
    const {isIntersecting} = status[0];

    const shouldHideFloOptions = {
      shouldHide: isIntersecting,
      from: 'header'
    };

    this.events.broadcast('shouldHideFlo:isIntersecting', shouldHideFloOptions);
  }
}
